import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const STORAGE_KEY = 'trms_workbench_token';

export type AuthUser = {
  sub: string;
  scopes: ReadonlySet<string>;
  raw: string;
};

type AuthContextValue = {
  user: AuthUser | null;
  token: string | null;
  scopes: ReadonlySet<string>;
  login: (token: string) => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Decode only — signature verification is enforced by the backend on every request.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const base64 = parts[1]!.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(base64);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function extractScopes(payload: Record<string, unknown>): ReadonlySet<string> {
  const raw = payload['scopes'] ?? payload['scope'];
  if (Array.isArray(raw)) {
    return new Set(raw.map(String));
  }
  if (typeof raw === 'string') {
    return new Set(raw.split(' ').filter(Boolean));
  }
  return new Set<string>();
}

function buildUser(token: string): AuthUser | null {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  const sub = typeof payload['sub'] === 'string' ? payload['sub'] : 'unknown';
  return {
    sub,
    scopes: extractScopes(payload),
    raw: token,
  };
}

type AuthProviderProps = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    return buildUser(stored);
  });

  const token = user?.raw ?? null;

  useEffect(() => {
    if (user) {
      localStorage.setItem(STORAGE_KEY, user.raw);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [user]);

  const login = useCallback((rawToken: string) => {
    const built = buildUser(rawToken);
    if (built) setUser(built);
  }, []);

  const logout = useCallback(() => {
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      scopes: user?.scopes ?? new Set<string>(),
      login,
      logout,
    }),
    [user, token, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
